import { createClientComponentClient } from '@supabase/auth-helpers-nextjs';
import { Database } from '@/types/supabase';
import { TeamMemberRole } from '@/types/database';
import { createPermissionChecker, Permission } from '@/lib/permissions/team-permissions';

/**
 * Check if a user has access to a specific vendor's data
 * 
 * Access is granted if:
 * 1. User is an admin
 * 2. User is the vendor owner (user_id matches vendor's user_id)
 * 3. User's profile has the same vendor_id as the requested vendor
 * 4. User is an active team member of the vendor
 * 
 * @param userId The user's ID
 * @param vendorId The vendor ID to check access for
 * @returns Promise<boolean> Whether the user has access
 */
export async function hasVendorAccess(userId: string, vendorId: string): Promise<boolean> {
  if (!userId || !vendorId) return false;
  
  const supabase = createClientComponentClient<Database>();
  
  try {
    // First, check if user is an admin (admins have access to all vendors)
    const { data: profileData, error: profileError } = await supabase
      .from('profiles')
      .select('role, vendor_id')
      .eq('id', userId)
      .single();
    
    if (profileError) {
      console.error('Error checking user role:', profileError);
      return false;
    }
    
    // Admin has access to all vendors
    if (profileData.role === 'admin') {
      return true;
    }
    
    // Check if user is the vendor owner
    const { data: vendorData, error: vendorError } = await supabase
      .from('vendors')
      .select('user_id')
      .eq('id', vendorId)
      .single();
    
    if (vendorError) {
      console.error('Error checking vendor owner:', vendorError);
      return false;
    }
    
    // User is the vendor owner
    if (vendorData.user_id === userId) {
      return true;
    }
    
    // Check if user's profile has the same vendor_id
    if (profileData.vendor_id === vendorId) {
      return true;
    }
    
    // Check if user is an active team member of the vendor
    const { data: teamMember, error: teamError } = await supabase
      .from('team_members')
      .select('id, role, status')
      .eq('vendor_id', vendorId)
      .eq('user_id', userId)
      .eq('status', 'active')
      .single();
    
    if (!teamError && teamMember) {
      return true;
    }
    
    // No access
    return false;
  } catch (error) {
    console.error('Error checking vendor access:', error);
    return false;
  }
}

/**
 * Get all vendors that a user has access to
 * 
 * @param userId The user's ID
 * @returns Promise<string[]> Array of vendor IDs the user has access to
 */
export async function getAccessibleVendors(userId: string): Promise<string[]> {
  if (!userId) return [];
  
  const supabase = createClientComponentClient<Database>();
  
  try {
    // First, check user's role and vendor_id
    const { data: profileData, error: profileError } = await supabase
      .from('profiles')
      .select('role, vendor_id')
      .eq('id', userId)
      .single();
    
    if (profileError) {
      console.error('Error checking user profile:', profileError);
      return [];
    }
    
    // Admin has access to all vendors
    if (profileData.role === 'admin') {
      const { data: allVendors, error: vendorsError } = await supabase
        .from('vendors')
        .select('id');
      
      if (vendorsError || !allVendors) {
        console.error('Error fetching all vendors:', vendorsError);
        return [];
      }
      
      return allVendors.map(vendor => vendor.id);
    }
    
    // For vendors, get their own vendor ID and any vendor they're associated with
    if (profileData.role === 'vendor') {
      const vendorIds: string[] = [];
      
      // Check if user is a vendor owner
      const { data: ownedVendor, error: ownedError } = await supabase
        .from('vendors')
        .select('id')
        .eq('user_id', userId);
      
      if (!ownedError && ownedVendor && ownedVendor.length > 0) {
        vendorIds.push(...ownedVendor.map(vendor => vendor.id));
      }
      
      // Add the vendor_id from profile if it exists and is not already included
      if (profileData.vendor_id && !vendorIds.includes(profileData.vendor_id)) {
        vendorIds.push(profileData.vendor_id);
      }
      
      // Check for team memberships
      const { data: teamMemberships, error: teamError } = await supabase
        .from('team_members')
        .select('vendor_id')
        .eq('user_id', userId)
        .eq('status', 'active');
      
      if (!teamError && teamMemberships && teamMemberships.length > 0) {
        const teamVendorIds = teamMemberships.map(tm => tm.vendor_id);
        teamVendorIds.forEach(vendorId => {
          if (!vendorIds.includes(vendorId)) {
            vendorIds.push(vendorId);
          }
        });
      }
      
      return vendorIds;
    }
    
    // For customers, they only have access if they have a vendor_id in their profile or team membership
    const customerVendorIds: string[] = [];
    
    if (profileData.vendor_id) {
      customerVendorIds.push(profileData.vendor_id);
    }
    
    // Check for team memberships
    const { data: teamMemberships, error: teamError } = await supabase
      .from('team_members')
      .select('vendor_id')
      .eq('user_id', userId)
      .eq('status', 'active');
    
    if (!teamError && teamMemberships && teamMemberships.length > 0) {
      const teamVendorIds = teamMemberships.map(tm => tm.vendor_id);
      teamVendorIds.forEach(vendorId => {
        if (!customerVendorIds.includes(vendorId)) {
          customerVendorIds.push(vendorId);
        }
      });
    }
    
    return customerVendorIds;
  } catch (error) {
    console.error('Error getting accessible vendors:', error);
    return [];
  }
}

/**
 * Get user's team member information for a specific vendor
 * 
 * @param userId The user's ID
 * @param vendorId The vendor ID
 * @returns Promise<{role: TeamMemberRole, permissions: any, isOwner: boolean} | null>
 */
export async function getUserTeamInfo(userId: string, vendorId: string): Promise<{
  role: TeamMemberRole;
  permissions: Record<string, boolean>;
  isOwner: boolean;
  isTeamMember: boolean;
} | null> {
  if (!userId || !vendorId) return null;
  
  const supabase = createClientComponentClient<Database>();
  
  try {
    // Check if user is admin
    const { data: profileData, error: profileError } = await supabase
      .from('profiles')
      .select('role')
      .eq('id', userId)
      .single();
    
    if (profileError) {
      console.error('Error checking user profile:', profileError);
      return null;
    }
    
    // Admin has full access
    if (profileData.role === 'admin') {
      return {
        role: 'admin',
        permissions: {},
        isOwner: false,
        isTeamMember: false,
      };
    }
    
    // Check if user is vendor owner
    const { data: vendorData, error: vendorError } = await supabase
      .from('vendors')
      .select('user_id')
      .eq('id', vendorId)
      .single();
    
    if (!vendorError && vendorData && vendorData.user_id === userId) {
      return {
        role: 'admin', // Vendor owner has admin privileges
        permissions: {},
        isOwner: true,
        isTeamMember: false,
      };
    }
    
    // Check team membership
    const { data: teamMember, error: teamError } = await supabase
      .from('team_members')
      .select('role, permissions, status')
      .eq('vendor_id', vendorId)
      .eq('user_id', userId)
      .eq('status', 'active')
      .single();
    
    if (teamError || !teamMember) {
      return null;
    }
    
    return {
      role: teamMember.role,
      permissions: (teamMember.permissions as Record<string, boolean>) || {},
      isOwner: false,
      isTeamMember: true,
    };
  } catch (error) {
    console.error('Error getting user team info:', error);
    return null;
  }
}

/**
 * Check if a user has a specific permission for a vendor
 * 
 * @param userId The user's ID
 * @param vendorId The vendor ID
 * @param permission The permission to check
 * @returns Promise<boolean> Whether the user has the permission
 */
export async function hasVendorPermission(
  userId: string, 
  vendorId: string, 
  permission: Permission
): Promise<boolean> {
  const teamInfo = await getUserTeamInfo(userId, vendorId);
  
  if (!teamInfo) return false;
  
  // Vendor owners and admins have all permissions
  if (teamInfo.isOwner || teamInfo.role === 'admin') {
    return true;
  }
  
  // Check team member permissions
  const permissionChecker = createPermissionChecker(teamInfo.role, teamInfo.permissions);
  return permissionChecker.hasPermission(permission);
}

/**
 * Check if a user can access a specific feature for a vendor
 * 
 * @param userId The user's ID
 * @param vendorId The vendor ID
 * @param feature The feature to check
 * @returns Promise<{canView: boolean, canManage: boolean}> Access levels
 */
export async function getVendorFeatureAccess(
  userId: string, 
  vendorId: string, 
  feature: 'orders' | 'inventory' | 'events' | 'payments' | 'products' | 'team' | 'settings' | 'reports'
): Promise<{canView: boolean, canManage: boolean}> {
  const teamInfo = await getUserTeamInfo(userId, vendorId);
  
  if (!teamInfo) {
    return { canView: false, canManage: false };
  }
  
  // Vendor owners and admins have full access
  if (teamInfo.isOwner || teamInfo.role === 'admin') {
    return { canView: true, canManage: true };
  }
  
  // Check team member permissions
  const permissionChecker = createPermissionChecker(teamInfo.role, teamInfo.permissions);
  
  return {
    canView: permissionChecker.canViewFeature(feature),
    canManage: permissionChecker.canManageFeature(feature),
  };
}

/**
 * Check if user is a team admin or vendor owner
 * 
 * @param userId The user's ID
 * @param vendorId The vendor ID
 * @returns Promise<boolean> Whether user has admin privileges
 */
export async function isVendorAdmin(userId: string, vendorId: string): Promise<boolean> {
  const teamInfo = await getUserTeamInfo(userId, vendorId);
  return teamInfo ? (teamInfo.isOwner || teamInfo.role === 'admin') : false;
}
